import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TeamBadge } from "./TeamBadge";
import { Clock, ChartLineUp, CaretRight } from "@phosphor-icons/react";
import { kickoffTimeTz, kickoffDate } from "../lib/datetime";
import { useHighConfidence } from "../lib/useHighConfidence";
import { FollowBell } from "./FollowBell";

export const MatchCard = ({ match, wide }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hc = useHighConfidence();
  const live = match.is_live;
  const finished = match.is_finished;
  const hasScore = match.score && match.score.home != null && match.score.away != null;
  const to = match.is_live
    ? `/live/${match.match_id}`
    : `/match/${String(match.match_id).startsWith("fx_") ? match.match_id : `fx_${match.match_id}`}`;
  const showHc = match.band === "excellent" && hc && hc.sample >= 10 && hc.accuracy_pct != null;

  return (
    <button
      onClick={() => navigate(to)}
      data-testid={`match-card-${match.match_id}`}
      className={`hm-card text-left p-4 transition-transform duration-200 hover:-translate-y-1 hover:hm-card-active ${
        wide ? "w-full" : "min-w-[240px] snap-start"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gold/80 truncate max-w-[45%]">{match.competition}</span>
        <div className="flex items-center gap-2 shrink-0">
          {live ? (
            <span className="flex items-center gap-1 text-[10px] font-bold text-perf-danger" data-testid={`live-badge-${match.match_id}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-perf-danger animate-pulse" />
              {match.status?.elapsed ? `${match.status.elapsed}'` : t("matchcard.live")}
            </span>
          ) : finished ? (
            <span className="text-[10px] font-bold text-white/40">{t("matchcard.finished")}</span>
          ) : (
            <span className="flex items-center gap-1 text-[11px] text-white/50">
              <Clock size={12} weight="bold" /> {kickoffTimeTz(match)}
            </span>
          )}
          <FollowBell match={match} size={15} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
          <TeamBadge team={match.home} size={40} />
          <span className="text-xs font-semibold text-white/80 text-center w-full leading-tight">{match.home.name}</span>
        </div>

        {hasScore ? (
          <div className="flex flex-col items-center">
            <span className="font-numeral font-black text-xl" style={{ color: live ? "#00E676" : "#fff" }}>
              {match.score.home} - {match.score.away}
            </span>
          </div>
        ) : (
          <span className="font-numeral font-black text-white/30 text-lg">{t("matchcard.vs")}</span>
        )}

        <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
          <TeamBadge team={match.away} size={40} />
          <span className="text-xs font-semibold text-white/80 text-center w-full leading-tight">{match.away.name}</span>
        </div>
      </div>
      <div className="mt-3 text-center text-[10px] font-medium text-white/40">{kickoffDate(match)}</div>
      {showHc && (
        <span
          role="button"
          tabIndex={0}
          data-testid={`hc-match-badge-${match.match_id}`}
          onClick={(e) => { e.stopPropagation(); navigate("/performance"); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate("/performance"); } }}
          className="mt-2 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 cursor-pointer"
          style={{ background: "rgba(0,230,118,0.10)", border: "1px solid rgba(0,230,118,0.4)" }}
        >
          <ChartLineUp size={13} weight="fill" color="#00E676" className="shrink-0" />
          <span className="text-[10px] leading-tight text-white/70">
            <span className="font-bold text-perf-excellent">{t("matchsheet.hc_badge_title")}</span>
            {" · "}{t("matchsheet.hc_badge_stat", { pct: hc.accuracy_pct })}
          </span>
          <CaretRight size={11} weight="bold" color="#00E676" className="ml-auto shrink-0" />
        </span>
      )}
    </button>
  );
};
